import React, { useEffect, useRef, useState } from "react";
import TrafficLights from "../TrafficLights";
import WindowFrame from "../WindowFrame";
import useGenie from "../../hooks/useGenie";
import usePlayer from "../../hooks/usePlayer";
import { CONTEXTS } from "../../data/tracks";
import Library from "./Library";
import PlaylistPage from "./PlaylistPage";
import SearchPage from "./SearchPage";
import NowPlayingView from "./NowPlayingView";
import PlayerBar from "./PlayerBar";
import { BackIcon, BellIcon, BrowseIcon, ClearIcon, ForwardIcon, FriendsIcon, HomeIcon, SearchIcon } from "./icons";

// Spotify's typeface isn't free to use; Figtree is the closest open one. Loaded when Spotify opens.
const FONT = "https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700;800;900&display=swap";
const loadFont = () => {
  if (document.getElementById("spotify-font")) return;
  const link = document.createElement("link");
  link.id = "spotify-font";
  link.rel = "stylesheet";
  link.href = FONT;
  document.head.appendChild(link);
};

// It opens as Sylvester's was: ICEMAN, found by searching "ice".
const START = { stack: [{ list: "iceman" }], at: 0 };
const START_QUERY = "ice";

// Default frame: up to 1180 x 740, a little right of center.
const spotifyFrame = (ws) => {
  const w = Math.min(1180, ws.w - 80);
  const h = Math.min(740, ws.h - 32);
  return { x: (ws.w - w) / 2 + 20, y: (ws.h - h) / 2, w, h };
};

// Spotify, like Sylvester's: his library, the album he had open, what's playing beside it, and the
// player along the bottom. It plays through the site's one player, so the menu bar and Control
// Center show the same song.
const SpotifyWindow = ({ workspace, open, front, onFocus, onClose }) => {
  const [exit, setExit] = useState("closed");
  const genie = useGenie({ app: "Spotify", open, exit });
  const [maximized, setMaximized] = useState(false);
  const [history, setHistory] = useState(START);
  const [query, setQuery] = useState(START_QUERY);
  const [nowPlaying, setNowPlaying] = useState(true); // the Now Playing view beside the page
  const { playlist } = usePlayer();
  const content = useRef(null);
  const page = history.stack[history.at];

  useEffect(() => {
    if (open) loadFont();
  }, [open]);

  // A new page starts at its top.
  useEffect(() => {
    if (content.current) content.current.scrollTop = 0;
  }, [history]);

  const go = (entry) =>
    setHistory((h) => {
      const here = h.stack[h.at];
      if (here.list === entry.list && Boolean(here.search) === Boolean(entry.search)) return h;
      return { stack: [...h.stack.slice(0, h.at + 1), entry], at: h.at + 1 };
    });
  const step = (delta) => setHistory((h) => ({ ...h, at: Math.max(0, Math.min(h.stack.length - 1, h.at + delta)) }));

  const search = (text) => {
    setQuery(text);
    if (text.trim()) go({ search: true });
    else if (page.search) step(-1);
  };

  const hide = (how) => {
    setExit(how);
    if (how === "closed") {
      setMaximized(false);
      setHistory(START);
      setQuery(START_QUERY);
      setNowPlaying(true);
    }
    onClose();
  };

  const state = open ? "nw--open" : `nw--${exit}`;
  return (
    <WindowFrame
      workspace={workspace}
      initial={spotifyFrame}
      min={{ w: 760, h: 480 }}
      titlebar={64}
      open={open}
      front={front}
      zoomed={maximized}
      onFocus={onFocus}
      onToggleZoom={() => setMaximized((m) => !m)}
    >
      <div
        role="dialog"
        aria-label="Spotify"
        aria-hidden={!open}
        ref={genie.ref}
        className={`nw sp ${state} ${maximized ? "nw--max" : ""} ${front ? "is-front" : ""} ${
          genie.hidden ? "is-genie-hidden" : ""
        }`}
      >
        <div className="traffic">
          <TrafficLights
            onClose={() => hide("closed")}
            onMinimize={() => genie.minimize(() => hide("minimized"))}
            onPrepare={genie.prepare}
            onZoom={() => setMaximized(!maximized)}
            zoomed={maximized}
          />
        </div>

        <header className="sp-top">
          <div className="sp-nav">
            <button type="button" onClick={() => step(-1)} disabled={history.at === 0} aria-label="Go back">
              <BackIcon />
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              disabled={history.at === history.stack.length - 1}
              aria-label="Go forward"
            >
              <ForwardIcon />
            </button>
          </div>
          <div className="sp-top-center">
            <button type="button" className="sp-home" onClick={() => go({ list: "iceman" })} aria-label="Home">
              <HomeIcon />
            </button>
            <label className="sp-search">
              <SearchIcon className="sp-search-icon" />
              <input
                value={query}
                onChange={(e) => search(e.target.value)}
                onFocus={() => query.trim() && go({ search: true })}
                placeholder="What do you want to play?"
                aria-label="What do you want to play?"
                spellCheck={false}
              />
              {query && (
                <button type="button" className="sp-search-clear" onClick={() => search("")} aria-label="Clear search">
                  <ClearIcon />
                </button>
              )}
              <span className="sp-search-divider" aria-hidden="true" />
              <span className="sp-search-browse" aria-hidden="true">
                <BrowseIcon />
              </span>
            </label>
          </div>
          <div className="sp-top-right" aria-hidden="true">
            <span className="sp-bell">
              <BellIcon />
            </span>
            <FriendsIcon className="sp-friends" />
            <span className="sp-avatar">
              <span>S</span>
            </span>
          </div>
        </header>

        <div className="sp-body">
          <Library page={page.list} playingId={playlist.id} onOpen={(list) => go({ list })} />
          <main className="sp-panel sp-content" ref={content}>
            {page.search ? (
              <SearchPage query={query} onOpen={(list) => go({ list })} />
            ) : (
              <PlaylistPage key={page.list} list={CONTEXTS[page.list]} scroller={content} />
            )}
          </main>
          {nowPlaying && <NowPlayingView onClose={() => setNowPlaying(false)} />}
        </div>

        <PlayerBar onToggleNowPlaying={() => setNowPlaying((v) => !v)} nowPlaying={nowPlaying} />
      </div>
    </WindowFrame>
  );
};

export default SpotifyWindow;
